import type { Variants } from "framer-motion";

/** Cubic-bezier easings (mirror the CSS --ease-* tokens). */
type Bezier = [number, number, number, number];
export const easeOut: Bezier = [0.22, 0.61, 0.36, 1];
export const easeInOut: Bezier = [0.65, 0, 0.35, 1];

/** Durations in seconds. Editorial Heritage = slow, weighted, refined. */
export const duration = { fast: 0.18, base: 0.42, slow: 0.66, page: 0.5 };

/** Matches the legacy CSS `premium-rise` translateY for visual continuity. */
export const REVEAL_Y = 22;

export const fadeUp: Variants = {
  hidden: { opacity: 0, y: REVEAL_Y },
  visible: { opacity: 1, y: 0, transition: { duration: duration.slow, ease: easeOut } },
};

export const fadeIn: Variants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: duration.base, ease: easeOut } },
};

/** Container that staggers its `staggerItem` children into view. */
export const staggerContainer = (stagger = 0.09, delayChildren = 0.05): Variants => ({
  hidden: {},
  visible: { transition: { staggerChildren: stagger, delayChildren } },
});

export const staggerItem: Variants = fadeUp;

/** Orchestrated hero entrance — plays once on load (not on scroll). The
 *  content column staggers its lines in over a slow, weighted curve. */
export const heroContainer: Variants = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.085, delayChildren: 0.12 } },
};
export const heroItem: Variants = {
  hidden: { opacity: 0, y: 26 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.72, ease: easeOut } },
};

/** Editorial image reveal — a clip wipes the frame open from the top down
 *  while it fades in. clip-path + opacity only, so it never spills past a
 *  rounded/gilt frame (no transform to overflow the container). */
export const imageReveal: Variants = {
  hidden: { opacity: 0, clipPath: "inset(0% 0% 30% 0%)" },
  visible: {
    opacity: 1,
    clipPath: "inset(0% 0% 0% 0%)",
    transition: { duration: 0.9, ease: easeOut },
  },
};

/** Page-transition variants for the route AnimatePresence.
 *  Opacity-only on purpose: a lingering transform on the page wrapper would
 *  make position:fixed overlays inside a page (catalogue lightbox, photo
 *  viewer) anchor to the wrapper instead of the viewport. */
export const pageVariants: Variants = {
  initial: { opacity: 0 },
  enter: { opacity: 1, transition: { duration: duration.page, ease: easeOut } },
  exit: { opacity: 0, transition: { duration: 0.28, ease: easeInOut } },
};
